import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import {
    WireAccount,
    WireCompany,
    WireIsoService,
    WireTemplateQueryDto,
    WireTemplateStatus,
    WiresService,
} from '@treasury/domain/wires';
import { TmBaseComponent } from '@treasury/presentation';
import type { SelectItemVm, TmValueChanged } from '@treasury/presentation/components/forms';
import { type DateRange, DateRangeType } from '@treasury/presentation/components/tm-date-range';
import { mapDateRangeToDto } from '@treasury/presentation/components/tm-date-range.mappings';
import '@treasury/presentation/components/tm-footer';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import '@treasury/presentation/components/tm-multi-select';
import '@treasury/presentation/components/tm-text-field';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { wireTemplateStatuses } from '../../data/wire-template-status-items';

import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-bottom-sheet';
// eslint-disable-next-line no-duplicate-imports
import '@treasury/presentation/components/tm-date-range';

type WireStatus = NonNullable<WireTemplateQueryDto['statuses']>[0];

const defaultCreatedDateRange: DateRange = {
    rangeType: DateRangeType.None,
};

export const tagName = 'wire-templates-filters-sheet';
@customElement(tagName)
export class WireTemplatesFiltersSheet extends TmBaseComponent {
    @property({ type: Boolean })
    public open = false;

    @InjectProperty()
    declare private wiresService: WiresService;

    @InjectProperty()
    declare private readonly wireIsoService: WireIsoService;

    private _templateSelection = false;

    @state()
    private templateName = '';

    @state()
    private beneficiaryName = '';

    @state()
    private wireCompanies: WireCompany[] = [];

    @state()
    private wireCompaniesSelected: WireCompany[] = [];

    @state()
    private statusItems = [...wireTemplateStatuses];

    @state()
    private statusItemsSelected: WireTemplateStatus[] = [];

    @state()
    private debitAccounts: WireAccount[] = [];

    @state()
    private debitAccountsSelected: WireAccount[] = [];

    @state()
    private createdDateRange: DateRange = defaultCreatedDateRange;

    @state()
    private wireIsoLabels = WireIsoService.legacyLabels;

    @DeepReactive()
    private validState = {
        wireCompany: true,
        status: true,
        debitAccount: true,
        createdDate: true,
    };

    @property({ type: Boolean })
    public get templateSelection() {
        return this._templateSelection;
    }

    public set templateSelection(value) {
        this._templateSelection = value;
        this.statusItems = this._templateSelection ? ['Ready'] : wireTemplateStatuses;
        this.statusItemsSelected = [...this.statusItems];
    }

    public render() {
        return html`<tm-bottom-sheet
            .open=${this.open}
            @close=${() => {
                this.dispatchEvent(new CustomEvent('close'));
                this.open = false;
            }}
        >
            <p slot="header-center">Filter Wire Templates</p>
            <p slot="header-right"></p>
            <tm-body>
                <form>
                    <tm-text-field
                        label="Template Name"
                        .initial=${this.templateName}
                        @value-changed=${(e: TmValueChanged<string>) => {
                            const { value, isValid } = e.detail;
                            if (value && isValid) {
                                this.templateName = value;
                            }
                        }}
                    ></tm-text-field>
                    <tm-text-field
                        label="${this.wireIsoLabels.creditor} Name"
                        .initial=${this.beneficiaryName}
                        @value-changed=${(e: TmValueChanged<string>) => {
                            const { value, isValid } = e.detail;
                            if (value && isValid) {
                                this.beneficiaryName = value;
                            }
                        }}
                    ></tm-text-field>
                    <tm-multi-select
                        label="Wire Company"
                        .allowSelectAll=${true}
                        .allSelectedText=${'All Wire Companies'}
                        .items=${this.wireCompanies}
                        .initial=${[...this.wireCompaniesSelected]}
                        .mapItem=${mapWireCompanyToVm}
                        @value-changed=${(e: TmValueChanged<WireCompany[]>) => {
                            this.wireCompaniesSelected = e.detail.value || [];
                            this.validState.wireCompany = this.wireCompaniesSelected.length > 0;
                        }}
                    ></tm-multi-select>
                    ${this.renderStatusSelect()}
                    <tm-multi-select
                        label="Debit Account"
                        .allowSelectAll=${true}
                        .allSelectedText=${'All Debit Accounts'}
                        .items=${this.debitAccounts}
                        .initial=${[...this.debitAccountsSelected]}
                        .mapItem=${mapDebitAccountToVm}
                        @value-changed=${(e: TmValueChanged<WireAccount[]>) => {
                            this.debitAccountsSelected = e.detail.value || [];
                            this.validState.debitAccount = this.debitAccountsSelected.length > 0;
                        }}
                    ></tm-multi-select>
                    <tm-date-range
                        label="Created Date"
                        .initial=${this.createdDateRange}
                        @value-changed=${(e: TmValueChanged<DateRange>) => {
                            const { isValid, value } = e.detail;
                            this.validState.createdDate = isValid;
                            if (value) {
                                this.createdDateRange = value;
                            }
                        }}
                        .maxDate=${new Date()}
                    ></tm-date-range>
                </form>
            </tm-body>
            <tm-footer
                .buttonConfig=${[
                    {
                        text: 'Apply Filters',
                        onClick: () => {
                            this.onApplyFilters();
                        },
                        disabled: Object.values(this.validState).some(isValid => !isValid),
                    },
                    {
                        importance: 'secondary',
                        text: 'Reset',
                        onClick: () => {
                            this.onResetForm();
                        },
                    },
                ] as ButtonConfig[]}
            >
            </tm-footer>
        </tm-bottom-sheet>`;
    }

    protected async firstUpdated() {
        this.wireIsoLabels = await this.wireIsoService.getLabels();
        this.wireCompanies = await this.wiresService.getWireCompanies(true);
        this.debitAccounts =
            (await this.wiresService.fetchTransferAccounts()) as unknown as WireAccount[];
        this.onResetForm();
        this.onApplyFilters();
    }

    private onResetForm() {
        this.templateName = '';
        this.beneficiaryName = '';
        this.wireCompaniesSelected = [...this.wireCompanies];
        this.debitAccountsSelected = [...this.debitAccounts];
        this.statusItemsSelected = [...this.statusItems];
        this.createdDateRange = { ...defaultCreatedDateRange };
    }

    private toDto() {
        const {
            operator: createdDateType,
            dateFrom: createdDateStart,
            dateTo: createdDateEnd,
            specificDate: createdDate,
        } = mapDateRangeToDto(this.createdDateRange);

        return {
            ...(this.templateName && { templateName: this.templateName }),
            ...(this.beneficiaryName && { beneficiaryName: this.beneficiaryName }),
            wireCompanies: this.wireCompaniesSelected.map(item => item.toDto()),
            statuses: [...this.statusItemsSelected.map(mapWireTemplateStatusToDto)],
            debitAccounts: [...this.debitAccountsSelected],
            createdDateType: createdDateType,
            ...(createdDateStart && { createdDateStart }),
            ...(createdDateEnd && { createdDateEnd }),
            ...(createdDate && { createdDate }),
            wireType: 'Both',
        };
    }

    private onApplyFilters() {
        this.dispatchEvent(new CustomEvent('close'));
        this.dispatchEvent(
            new CustomEvent('applyFilters', {
                detail: this.toDto(),
            })
        );
    }

    private renderStatusSelect() {
        if (this.statusItems.length <= 1) {
            return nothing;
        }
        return html`
            <tm-multi-select
                label="Status"
                .allowSelectAll=${true}
                .allSelectedText=${'All Wire Statuses'}
                .items=${this.statusItems}
                .initial=${[...this.statusItemsSelected]}
                @value-changed=${(e: TmValueChanged<WireTemplateStatus[]>) => {
                    this.statusItemsSelected = e.detail.value || [];
                    this.validState.status = this.statusItemsSelected.length > 0;
                }}
            ></tm-multi-select>
        `;
    }

    public static get styles() {
        return [
            css`
                :host {
                    --navbar-height: 60px;
                    height: 100%;
                }
                path {
                    fill: #ffffff;
                }
            `,
        ];
    }
}

function mapWireCompanyToVm(wireCompany: WireCompany): SelectItemVm<WireCompany> {
    return {
        label: wireCompany.name,
        value: wireCompany,
    };
}

function mapDebitAccountToVm(account: WireAccount): SelectItemVm<WireAccount> {
    return {
        label: account.accountDisplayLabel ?? '',
        value: account,
    };
}

function mapWireTemplateStatusToDto(status: WireTemplateStatus): WireStatus {
    return {
        name: status,
        isChecked: true,
    };
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireTemplatesFiltersSheet;
    }
}
